<template>
  <div class="game">
    <div v-if="isGameOn">
      <div class="header">
        <button v-if="!isGameMaster"
                class="btn"
                :disabled="myHand.length >= 3"
                @click="drawCard">Draw card
        </button>
        <div>
          <span class="label">Cards left:</span>
          <span>{{ gameState.drawPile.length }}</span>
        </div>
        <div>
          <span class="label">Discard pile size:</span>
          <span>{{ gameState.discardPile.length }}</span>
        </div>
        <div v-if="gameState.clockCardsPlayed.length > 0">
          <span class="label">Clocks:</span>
          <ul class="cards-list" :class="{ 'zoom': zoomOnClockCard }">
            <div class="background"></div>
            <li v-for="(card, i) in gameState.clockCardsPlayed" v-bind:key="card.uuid">
              <card-view :card="card"
                         :can-be-played="false"
                         :can-be-discarded="false"
                         :size="zoomOnClockCard && i === (gameState.clockCardsPlayed.length - 1) ? '' : 'sm'"></card-view>
            </li>
          </ul>
        </div>
        <button class="btn"
                @click="shuffle">Shuffle
        </button>
        <button class="btn" @click="isSpecialActionsMenuVisible = true">Special Actions
          <div class="full-screen"
               v-if="isSpecialActionsMenuVisible"
               @click.stop="isSpecialActionsMenuVisible = false"></div>
          <div class="menu" v-if="isSpecialActionsMenuVisible">
            <ul class="actions">
              <li @click.stop="chooseFactionCardFromDiscardPileAndReturnItToYourHand">
                Choose a faction card from the discard pile and return it to your hand
              </li>
              <li @click.stop="chooseCardFromDiscardPileToShuffleIntoTheDeck">
                Choose card from discard pile to shuffle into the deck
              </li>
              <li @click.stop="shuffleHandIntoDeck">
                Shuffle hand into deck
              </li>
              <li @click.stop="findGreenClockCardAndShuffleTheDeck">
                Find green clock card and shuffle the deck
              </li>
            </ul>
          </div>
        </button>
      </div>
      <hr>
      <h3>
        <span v-if="!isGameMaster">Me</span>
        <span class="badge" :style="`background: ${myColorIsRed ? 'red' : 'blue'}`">{{ myColorIsRed ? 'red' : 'blue' }}</span>
        <div class="command-points">
          <span>Command points:</span>
          <button v-if="!isGameMaster"
                  @click="addOneCommandPoint">+
          </button>
          <div id="my_command_points" v-text="myCommandPoints"></div>
          <button v-if="!isGameMaster"
                  @click="removeOneCommandPoint">-
          </button>
        </div>
      </h3>
      <div class="player">
        <div class="hand pane">
          <div class="badge"><span>Hand</span></div>
          <ul class="cards-list" v-if="myHand.length > 0">
            <li v-for="card in myHand" v-bind:key="card.uuid">
              <card-view :card="card"
                         :can-be-played="!isGameMaster"
                         :can-be-discarded="false"
                         :can-be-held-back="!isGameMaster && !card.noHoldBack && ((card.isRed && myColorIsRed) || (card.isBlue && myColorIsBlue))"
                         @played="playCard(card)"
                         @discarded="discard(card)"
                         @held-back="holdBack(card)"></card-view>
            </li>
          </ul>
          <div class="cards-list" v-else="">
            <div style="display: flex; align-items: center; justify-content: center; width: 100%;height: 21.5vw;">
              <i style="margin: auto 0;">No cards in your hand.</i>
            </div>
          </div>
        </div>
        <div class="held-back pane">
          <div class="badge"><span>Held back</span></div>
          <card-view v-if="myHeldBackCard != null"
                     :card="myHeldBackCard"
                     :can-be-played="true"
                     @played="playCard(myHeldBackCard)"></card-view>
        </div>
        <div class="played pane">
          <div class="badge"><span>Cards played</span></div>
          <template v-if="cardsPlayed.length > 0">
            <card-view :card="cardsPlayed[0]"
                       :can-be-chosen="isChoosingCardFromDiscardPile
                       || (isChoosingFactionCardFromDiscardPile
                        && ((isMaster && cardsPlayed[0].isRed === gameState.isMasterRed)
                        || (!isMaster && cardsPlayed[0].isRed === !gameState.isMasterRed)))"
                       :can-be-removed="true"
                       @chosen="onDiscardedCardClicked(cardsPlayed[0])"
                       @remove="() => { removeCardFromDeck(cardsPlayed[0]); }"></card-view>
            <ul class="cards-list">
              <li v-for="card in cardsPlayed.slice(1)" v-bind:key="card.uuid">
                <card-view :card="card"
                           size="sm"
                           :can-be-chosen="isChoosingCardFromDiscardPile
                           || (isChoosingFactionCardFromDiscardPile
                            && ((isMaster && card.isRed === gameState.isMasterRed)
                            || (!isMaster && card.isRed === !gameState.isMasterRed)))"
                           @chosen="onDiscardedCardClicked(card)"></card-view>
              </li>
            </ul>
          </template>
        </div>
      </div>
      <hr>
      <h3>
        <span v-if="!isGameMaster">Opponent</span>
        <span class="badge" :style="`background: ${!myColorIsRed ? 'red' : 'blue'}`">{{ !myColorIsRed ? 'red' : 'blue' }}</span>
        <div class="command-points">
          <span>Command points:</span>
          <div id="opponent_command_points" v-text="opponentCommandPoints"></div>
        </div>
      </h3>
      <div class="player">
        <div class="hand pane">
          <div class="badge"><span>Hand</span></div>
          <ul class="cards-list" v-if="opponentHand.length > 0">
            <li v-for="card in opponentHand" v-bind:key="card.uuid">
              <card-view :hidden="!isGameMaster"
                         :card="card"></card-view>
            </li>
          </ul>
          <div class="cards-list" v-else="">
            <div style="display: flex; align-items: center; justify-content: center; width: 100%;height: 21.5vw;">
              <i style="margin: auto 0;">No cards in their hand.</i>
            </div>
          </div>
        </div>
        <div class="held-back pane">
          <div class="badge"><span>Held back</span></div>
          <card-view v-if="opponentHeldBackCard != null"
                     :card="opponentHeldBackCard"
                     :hidden="!isGameMaster"></card-view>
        </div>
        <div class="played pane">
          <div class="badge"><span>Cards played</span></div>
          <template v-if="opponentPlayedCards.length > 0">
            <card-view :card="opponentPlayedCards[0]"
                       :can-be-chosen="isChoosingCardFromDiscardPile
                       || (isChoosingFactionCardFromDiscardPile
                        && ((isMaster && opponentPlayedCards[0].isRed === gameState.isMasterRed)
                        || (!isMaster && opponentPlayedCards[0].isRed === !gameState.isMasterRed)))"
                       @chosen="onDiscardedCardClicked(opponentPlayedCards[0])"></card-view>
            <ul class="cards-list">
              <li v-for="card in opponentPlayedCards.slice(1)" v-bind:key="card.uuid">
                <card-view :card="card"
                           size="sm"
                           :can-be-chosen="isChoosingCardFromDiscardPile
                           || (isChoosingFactionCardFromDiscardPile
                            && ((isMaster && card.isRed === gameState.isMasterRed)
                            || (!isMaster && card.isRed === !gameState.isMasterRed)))"
                           @chosen="onDiscardedCardClicked(card)"></card-view>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </div>
    <div v-else="" class="waiting">
      <span v-if="isGameMaster">
      Waiting for the players...
      </span>
      <span v-else-if="isMaster">
      Waiting for the other player...
      </span>
      <span v-else="">
      Joining...
      </span>
    </div>
    <!--    <div class="deck-content" v-if="!isGameOn && gameState != null">-->
    <!--      <ul class="cards-list">-->
    <!--        <card-view v-for="card in gameState.deck" v-bind:key="card.uuid" :card="card"></card-view>-->
    <!--      </ul>-->
    <!--    </div>-->
  </div>
</template>

<script>
import Peer from 'peerjs';
import GameState from '@/models/GameState';
import CardView from '@/components/CardView';
import { DateTime } from 'luxon';

const HANDSHAKE_MESSAGE = 'hi!';
const beep = new Audio('assets/sounds/notification.mp3');
const clock = new Audio('assets/sounds/clock.mp3');

export default {
  props: {
    gameName: { type: String },
    mode: { type: String },
    state: { type: Object, default: null },
  },
  components: {
    CardView,
  },
  data() {
    return {
      connections: [],
      internalMode: null,
      isGameOn: false,
      isChoosingCardFromDiscardPile: false,
      isChoosingFactionCardFromDiscardPile: false,
      lastClockCardPlayedAt: null,
      onDiscardedCardClicked: null,
      peer: null,
      peerID: null,
      log: '',
      gameState: null,
      isSpecialActionsMenuVisible: false,
      zoomOnClockCard: false,
      peerList: [],
      hasGameMaster: false,
    };
  },
  computed: {
    isMaster() {
      return this.peerID === this.masterID;
    },
    isGameMaster() {
      return this.peerID === this.gameMasterID;
    },
    myHand() {
      if (this.peerID == null) {
        return [];
      }
      if (this.isGameMaster) {
        return this.gameState.masterCards;
      }
      return this.peerID === this.masterID
          ? this.gameState.masterCards
          : this.gameState.slaveCards;
    },
    myHeldBackCard() {
      if (this.peerID == null) {
        return null;
      }
      if (this.isGameMaster) {
        return this.gameState.masterHeldBackCard;
      }
      return this.peerID === this.masterID
          ? this.gameState.masterHeldBackCard
          : this.gameState.slaveHeldBackCard;
    },
    opponentHeldBackCard() {
      if (this.peerID == null) {
        return [];
      }
      if (this.isGameMaster) {
        return this.gameState.slaveHeldBackCard;
      }
      return this.peerID !== this.masterID
          ? this.gameState.masterHeldBackCard
          : this.gameState.slaveHeldBackCard;
    },
    opponentHand() {
      if (this.peerID == null) {
        return [];
      }
      if (this.isGameMaster) {
        return this.gameState.slaveCards;
      }
      return this.peerID === this.slaveID
          ? this.gameState.masterCards
          : this.gameState.slaveCards;
    },
    cardsPlayed() {
      if (this.peerID == null) {
        return [];
      }
      if (this.isGameMaster) {
        return [...this.gameState.masterPlayedCards].reverse();
      }
      return this.peerID === this.slaveID
          ? [...this.gameState.slavePlayedCards].reverse()
          : [...this.gameState.masterPlayedCards].reverse();
    },
    opponentPlayedCards() {
      if (this.peerID == null) {
        return [];
      }
      if (this.isGameMaster) {
        return [...this.gameState.slavePlayedCards].reverse();
      }
      return this.peerID === this.masterID
          ? [...this.gameState.slavePlayedCards].reverse()
          : [...this.gameState.masterPlayedCards].reverse();
    },
    gameMasterID() {
      return `${this.gameName}-game-master`;
    },
    masterID() {
      return `${this.gameName}-master`;
    },
    slaveID() {
      return `${this.gameName}-slave`;
    },
    myColorIsRed() {
      return this.isGameMaster || (this.isMaster && this.gameState.isMasterRed);
    },
    myColorIsBlue() {
      return !this.myColorIsRed;
    },
    myCommandPoints() {
      if (this.peerID == null) {
        return null;
      }
      if (this.isGameMaster) {
        return this.gameState.masterCommandPoints;
      }
      return this.peerID === this.masterID
          ? this.gameState.masterCommandPoints
          : this.gameState.slaveCommandPoints;
    },
    opponentCommandPoints() {
      if (this.peerID == null) {
        return null;
      }
      if (this.isGameMaster) {
        return this.gameState.slaveCommandPoints;
      }
      return this.peerID !== this.masterID
          ? this.gameState.masterCommandPoints
          : this.gameState.slaveCommandPoints;
    },
    clockCardsPlayed() {
      return this.gameState != null
          ? this.gameState.clockCardsPlayed
          : [];
    },
  },
  methods: {
    //
    // P2P
    //
    initPeer: function (withGameMaster) {
      if (withGameMaster) {
        this.peerID = this.gameMasterID;
      } else {
        this.peerID = this.internalMode === 'join' ? this.slaveID : this.masterID;
      }
      this.peer = new Peer(this.peerID, {
        host: 'apps.iwouldliketorage.com',
        port: 9000,
        debug: 2,
        path: '/myapp'
      });
      this.peer.on('error', (e) => {
        console.log('peer error');
        console.log(e);
        if (e.message.includes('Could not connect to peer')) {
          if (this.peerID === this.slaveID) {
            this.openConnection(this.lastTargetPeerID === this.masterID ? this.gameMasterID : this.masterID);
          } else {
            this.openConnection(this.lastTargetPeerID === this.slaveID ? this.gameMasterID : this.slaveID);
          }
        } else {
          this.$emit('duplicate-game');
          setTimeout(this.initPeer, 1000);
          this.internalMode = 'rejoin'; // Trying to swap peer id...
        }
      });
      this.peer.on('open', (id) => {
        console.log('peer open');
        console.log(`My peer ID is: ${id}`);
        if (id === this.slaveID
            || (this.gameState != null && this.gameState.hasGameMaster && !this.isGameMaster)
            || this.internalMode === 'rejoin') {
          this.openConnection();
        }
      });
      this.peer.on('connection', (conn) => {
        console.log(`peer connection from ${conn.peer}`);

        conn.on('data', data => {
          console.log('conn data');

          const state = JSON.parse(data);

          // Ignore messages from self.
          if (state.from === this.peerID) {
            return;
          }

          if (state.message === HANDSHAKE_MESSAGE) {
            this.openConnection(state.from);
            if (!this.peerList.some(x => x === state.from)) {
              this.peerList.push(state.from);
            }
          } else if (state.gameState != null && this.isGameMaster) {
            this.gameState = GameState.fromObject(state.gameState, new GameState());
            console.log(`Relay game state from ${conn.peer}`);
            this.sendState(conn.peer);
          } else {
            let shouldBounce = false;
            if (this.isGameOn
                && this.gameState != null
                && ((this.isMaster && this.opponentCommandPoints !== state.gameState.slaveCommandPoints)
                    || (!this.isMaster && this.opponentCommandPoints !== state.gameState.masterCommandPoints))) {
              shouldBounce = true;
            }
            this.gameState = GameState.fromObject(state.gameState, new GameState());
            if (!this.isGameOn) {
              this.isGameOn = true;
            } else {
              this.beep();
            }

            if (shouldBounce) {
              setTimeout(() => {
                this.bounceElement(window.document.getElementById('opponent_command_points'));
              }, 100);
            }
          }
        });
      });

      setTimeout(() => {
        if (this.internalMode === 'join' && !this.isGameOn) {
          this.openConnection();
          console.log('Trying to open connection...');
        }
      }, 1000);
    },
    initGame() {
      // this.gameState = new GameState();
      this.isGameOn = true;
      this.gameState.drawPileIDs = this.gameState.deck.sort(() => Math.random() > 0.5 ? 1 : -1).map(x => x.uuid);
      this.sendState();
    },
    openConnection(targetPeerID = null) {
      if (targetPeerID == null) {
        targetPeerID = this.gameMasterID;
      }
      this.lastTargetPeerID = targetPeerID;
      console.log(`connecting to ${targetPeerID}...`);
      let conn = this.peer.connect(targetPeerID);
      this.connections = [...this.connections.filter(x => x.peer !== conn.peer), conn];

      conn.on('error', (e) => {
        console.log('conn error');
        console.log(e);
      });
      conn.on('open', () => {
        console.log('conn open');
        if (!this.hasGameMaster) {
          this.hasGameMaster = conn.peer === this.gameMasterID;
        }
        if (!this.isGameOn
            && ((this.gameState != null && this.gameState.hasGameMaster && !this.isGameMaster)
                || this.peerID === this.slaveID
                || this.internalMode === 'rejoin')) {

          const payload = {
            message: HANDSHAKE_MESSAGE,
            from: this.peerID,
          };
          conn.send(JSON.stringify(payload));
        } else {
          console.log(`peerList.length: ${this.peerList.length}`);
          console.log(`isGameMaster: ${this.isGameMaster}`);
          console.log(`peerID: ${this.peerID}`);
          console.log(`isGameOn: ${this.isGameOn}`);

          if (this.gameState != null
              && ((this.hasGameMaster && this.isGameMaster && this.peerList.length === 2)
                  || (!this.hasGameMaster && this.peerID === this.masterID))
              && !this.isGameOn) {
            this.initGame();
          }
        }
      });
    },
    sendState(relayFrom = null) {
      console.log('sending state...');
      const state = JSON.stringify({ from: this.peerID, gameState: this.gameState });
      this.connections.filter(x => relayFrom == null || x.peer !== relayFrom).forEach(conn => conn.send(state));
    },
    //
    // Actions
    //
    drawCard() {
      if (this.isGameMaster) {
        return;
      }
      if (this.isMaster) {
        this.gameState.drawForMaster();
      } else {
        this.gameState.drawForSlave();
      }
      this.sendState();
    },
    shuffle() {
      this.gameState.shuffle();
      this.sendState();
    },
    playCard(card) {
      this.gameState.play(card);
      if ((card.isRed && !this.myColorIsRed) || (card.isBlue && !this.myColorIsBlue)) {
        this.addOneCommandPoint();
      }
      this.sendState();
    },
    discard(card) {
      this.gameState.discard(card);
      this.sendState();
    },
    holdBack(card) {
      this.gameState.holdBack(card);
      this.sendState();
    },
    addOneCommandPoint() {
      if (this.peerID == null) {
        return;
      }
      if (this.myCommandPoints === 10) {
        return;
      }

      if (this.peerID === this.masterID) {
        this.gameState.masterCommandPoints += 1;
      } else {
        this.gameState.slaveCommandPoints += 1;
      }
      this.bounceElement(window.document.getElementById('my_command_points'));

      this.sendState();
    },
    removeOneCommandPoint() {
      if (this.peerID == null) {
        return;
      }
      if (this.peerID === this.masterID) {
        this.gameState.masterCommandPoints -= 1;
      } else {
        this.gameState.slaveCommandPoints -= 1;
      }

      this.bounceElement(window.document.getElementById('my_command_points'));

      this.sendState();
    },
    //
    // Interactions
    //
    bounceElement(elm) {
      if (!elm.classList.contains('bounce')) {
        elm.classList.add('bounce');
      }
      setTimeout(() => {
        elm.classList.remove('bounce');
      }, 500);
    },
    beep() {
      beep.play();
    },
    ticToc() {
      this.zoomOnClockCard = true;
      setTimeout(() => {
        clock.play();
      }, 500);

      setTimeout(() => {
        this.zoomOnClockCard = false;
      }, 3000);
    },
    //
    // Special actions
    //
    chooseFactionCardFromDiscardPileAndReturnItToYourHand() {
      this.isSpecialActionsMenuVisible = false;
      this.isChoosingFactionCardFromDiscardPile = true;
      this.onDiscardedCardClicked = (card) => {
        // console.log(card);
        this.isChoosingFactionCardFromDiscardPile = false;
        this.gameState.returnDiscardedCardToHand(this.isMaster, card);
        this.sendState();
      };
    },
    chooseCardFromDiscardPileToShuffleIntoTheDeck() {
      this.isSpecialActionsMenuVisible = false;
      this.isChoosingCardFromDiscardPile = true;
      this.onDiscardedCardClicked = (card) => {
        // console.log(card);
        this.isChoosingCardFromDiscardPile = false;
        this.gameState.shuffleDiscardedCardInDeck(card);
        this.sendState();
      };
    },
    shuffleHandIntoDeck() {
      this.isSpecialActionsMenuVisible = false;
      this.gameState.shuffleHandIntoDeck(this.isMaster);
      this.sendState();
    },
    findGreenClockCardAndShuffleTheDeck() {
      this.isSpecialActionsMenuVisible = false;
      this.gameState.findGreenClockCardAndShuffleTheDeck(this.isMaster);
      this.sendState();
    },
    removeCardFromDeck(card) {
      this.gameState.remove(card);
      this.sendState();
    },
  },
  watch: {
    state() {
      this.gameState = this.state;
    },
    mode() {
      this.internalMode = this.mode;
    },
    clockCardsPlayed() {
      if (this.gameState == null || this.gameState.clockCardsPlayed.length == 0) {
        return;
      }
      const playedAt = this.gameState.clockCardsPlayed[this.gameState.clockCardsPlayed.length - 1].playedAt;
      if (this.gameState.clockCardsPlayed.length > 0
          && this.gameState != null
          && (this.lastClockCardPlayedAt == null
              || !DateTime.fromISO(this.lastClockCardPlayedAt).equals(DateTime.fromISO(playedAt)))) {
        this.lastClockCardPlayedAt = playedAt;
        this.ticToc();
        console.log('tic toc');
      }
    },
  },
  beforeMount() {
    this.internalMode = this.mode;
  },
  mounted() {
    if (this.peer == null) {
      this.hasGameMaster = this.state != null ? this.state.hasGameMaster : false;
      this.initPeer(this.hasGameMaster);
    }
    this.gameState = this.state;
  },
};

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>

.game {
  padding-bottom: 3.2vw;
}

hr {
  margin: 1vw 0;
  border-color: #413a2966;
  border-width: 1px;
  border-bottom-width: 0;
}

h3 {
  background: #ffffff11;
  font-weight: bold;
  padding: 1vw;
  font-size: 1.4vw;
  color: #1c1911;
  margin-top: -1vw;
}

.cards-list {
  display: flex;
  flex-direction: row;
  list-style: none;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0;
  flex-wrap: wrap;

  .background {
    display: none;
  }

  &.zoom {
    .background {
      display: block;
      background: #00000033;
      position: fixed;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
    }

    li:last-of-type {
      position: fixed;
      width: 35vw;
      height: 45vw;
      top: 22.5vw;
      left: 32.5vw;
      transition: all .2s cubic-bezier(0, .77, .17, 1);
      z-index: 10;
    }
  }

  i {
    display: block;
    width: 100%;
    vertical-align: center;
    margin-top: 9vw;
    text-align: center;
    font-size: 1.9vw;
  }

  li {
    margin-right: 0.5vw;
    margin-bottom: 0.5vw;
    transition: all .2s cubic-bezier(0, .77, .17, 1);

    &:last-of-type {
      margin-right: 0;
    }
  }

  .card {

  }
}

.player {
  display: flex;

  h4 {
    font-family: 'Nixie One';
    margin: 0.5vw 0 0 0;
    padding: 0;
  }

  .hand {
    margin-right: 1vw;
    flex-shrink: 0;
  }

  .held-back {
    margin-right: 1vw;
    flex-basis: 27.5vw !important;
  }

  .played {
    flex-grow: 1;
  }

  .pane {
    flex-basis: 47vw;
    border-radius: 1vw;
    padding: 1vw 1vw 1vw;
    position: relative;
    border: solid 1px #00000022;
    background: #ffffff22;
    margin-top: 2vw;
    display: flex;
    align-items: flex-start;

    > .card {
      margin-right: 0.5vw;
    }

    &.alert {
      background: red;
    }
  }

  .badge {
    position: absolute;
    top: -2.6vw;
    left: 0vw;
    //background: #413a29;
    font-family: 'Montserrat';
    color: #ffffffbb;
    font-size: 1.2vw;
    font-weight: bold;
    padding: 0.3vw 1.2vw 0.4vw;
    border-radius: 0.6vw;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .too-many-cards {
    position: absolute;
    top: -2.6vw;
    right: 0vw;
    background: #ffffffee;
    color: red;
    font-size: 1.2vw;
    font-weight: bold;
    padding: 0.3vw 1.2vw 0.4vw;
    border-radius: 1vw;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }
}

.header {
  font-family: 'Montserrat';
  font-weight: bold;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  font-size: 1.4vw;

  > div {
    display: flex;
    align-items: center;
    flex-basis: 18vw;
    justify-content: flex-end;
  }

  .turn {
    //font-family: 'Nixie One';
  }

  button {
  }

  .cards-list {
    width: auto;
  }

  .label {
    margin-right: 0.6rem;
  }
}

.waiting {
  padding: 1.6vw 3.2vw;
  background: #00000033;
  color: #fff;
  border-radius: 0.5vw;
  font-size: 1.4vw;
  font-weight: 600;
  margin: 2vw auto auto;
  text-align: center;
}

.deck-content {
  .cards-list {
    justify-content: space-around;
    padding-top: 1.6rem;

    .card {
      margin-bottom: 1.6rem;
    }
  }
}

h3 {
  display: flex;
  align-items: center;
  font-family: 'Montserrat';

  .badge {
    border-radius: 1.6vw;
    padding: 0.3vw 1vw;
    color: #fff;
    font-size: 0.8em;
    box-shadow: 0 0.1rem 0.3rem #00000066;
    font-weight: 600;
    margin-left: 0.6rem;
    text-transform: capitalize;
  }

  .command-points {
    display: flex;
    float: right;
    flex-grow: 1;
    align-items: center;
    justify-content: flex-end;

    > div {
      background: #ffffff66;
      padding: 0.3vw 0.8vw;
      border-radius: 0.5vw;
      width: 2vw;
      text-align: center;
    }

    > * {
      margin-right: 0.5vw;

      &:last-child {
        margin-right: 0;
      }

      &:first-child {
        margin-right: 1vw;
      }
    }

    button {
      font-size: 1.6vw;
      padding: 0.1vw 1vw;
      cursor: pointer;
      border-radius: 0.8vw;
      border: none;
      box-shadow: 0 0.1vw 0.1vw 0.2vw #00000011;
      outline: none;
    }
  }
}

.btn {
  position: relative;

  .full-screen {
    opacity: 0.1;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    background: #000;
  }

  .menu {
    position: absolute;
    z-index: 11;
    top: 3.2vw;
    right: 0;

    ul.actions {
      background: #1c1911;
      color: #ffffff77;
      margin: 0;
      padding: 0;
      width: 38vw;
      text-align: left;
      list-style: none;

      li {
        cursor: pointer;
        padding: 1vw;
        border-bottom: solid 1px #ffffff22;

        &:hover {
          background: #ffffff33;
          color: #fff;
        }

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}
</style>
